// ENROLL — Medicare / Health Enrollment Agent dashboard. Lives in
// the health portal as the licensed agent's single window into what
// ENROLL is doing: who's enrollment-ready, who needs re-shop, SEP
// windows closing, and compliance breaches that must be reviewed
// personally.
//
// Mirrors RetainAgentView's structure (hero / bucket chips / two-column
// queue + escalations) so the operator's mental model is consistent
// across both agents.

import React from 'react';
import { readPersistedJwtDirect } from '../lib/authedFetch';
import { ShieldCheck, AlertCircle, Calendar, FileWarning, ChevronRight, CheckCircle2, Users } from 'lucide-react';
import { supabase } from '../lib/supabase';

async function apiFetch(path, init = {}) {
  const jwt = readPersistedJwtDirect();
  const res = await fetch(path, {
    ...init,
    headers: { 'content-type': 'application/json', ...(jwt ? { Authorization: `Bearer ${jwt}` } : {}), ...(init.headers || {}) },
  });
  const body = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(body?.error || `HTTP ${res.status}`);
  return body;
}

function fmtAgo(iso) {
  if (!iso) return '';
  const t = Date.parse(iso); if (!Number.isFinite(t)) return '';
  const mins = Math.round((Date.now() - t) / 60000);
  if (mins < 1)    return 'just now';
  if (mins < 60)   return `${mins}m ago`;
  if (mins < 1440) return `${Math.round(mins / 60)}h ago`;
  return `${Math.round(mins / 1440)}d ago`;
}
function fmtPct(v) {
  if (v == null) return '—';
  return `${Math.round(v * 100)}%`;
}

const URGENCY_META = {
  compliance: { label: 'Compliance', bg: 'bg-rose-50',   border: 'border-rose-300',   text: 'text-rose-900',    rank: 5 },
  critical:   { label: 'Critical',   bg: 'bg-red-50',    border: 'border-red-200',    text: 'text-red-800',     rank: 4 },
  high:       { label: 'High',       bg: 'bg-orange-50', border: 'border-orange-200', text: 'text-orange-800',  rank: 3 },
  normal:     { label: 'Normal',     bg: 'bg-amber-50',  border: 'border-amber-200',  text: 'text-amber-800',   rank: 2 },
  low:        { label: 'Low',        bg: 'bg-slate-50',  border: 'border-slate-200',  text: 'text-slate-700',   rank: 1 },
};

const AEP_PHASE_META = {
  pre:    { tone: 'from-sky-900 to-slate-900',     label: 'AEP — pre-window' },
  active: { tone: 'from-amber-700 to-slate-900',   label: 'AEP ACTIVE' },
  post:   { tone: 'from-slate-700 to-slate-900',   label: 'Off-season' },
};

export default function EnrollAgentView({ currentUser, onGotoTab = () => {}, showToast = () => {} }) {
  const [data, setData] = React.useState(null);
  const [loading, setLoading] = React.useState(true);

  const load = React.useCallback(async () => {
    if (!currentUser?.id) return;
    setLoading(true);
    try {
      const r = await apiFetch('/api/enroll/snapshot');
      setData(r);
    } catch (err) {
      showToast(`ENROLL load failed: ${err.message || err}`, 'error');
    } finally {
      setLoading(false);
    }
  }, [currentUser?.id, showToast]);

  React.useEffect(() => { load(); }, [load]);

  const resolveEscalation = async (esc, status, outcome) => {
    try {
      await apiFetch('/api/enroll/resolve-escalation', {
        method: 'POST',
        body: JSON.stringify({ id: esc.id, status, outcome }),
      });
      showToast(`Escalation ${status}.`);
      load();
    } catch (err) {
      showToast(`Could not resolve: ${err.message}`, 'error');
    }
  };

  if (loading || !data) {
    return (
      <div className="max-w-none mx-auto px-4 py-4">
        <div className="text-sm text-slate-500">Loading ENROLL…</div>
      </div>
    );
  }

  const { aep, summary, queues = {}, escalations = [] } = data;
  const phaseMeta = AEP_PHASE_META[aep?.phase] || AEP_PHASE_META.pre;
  const complianceCount = summary.compliance_breaches_open || 0;

  return (
    <div className="max-w-none mx-auto px-4 py-4 space-y-4">

      {/* Top-of-page red alert when breaches > 0. Per audit response §15
          Phase 5b ENROLL: "compliance breaches displayed in a non-alarming
          way. This should be a red banner at the top of every screen until
          resolved — not a tab the user has to navigate to." We don't paint
          every screen, but we paint the ENROLL agent view loudly + offer a
          single-click jump to the compliance scaffold below. */}
      {complianceCount > 0 && (
        <div className="bg-gradient-to-r from-red-600 to-red-700 text-white rounded-xl px-5 py-4 shadow-lg border-2 border-red-300 flex items-start gap-3 flex-wrap">
          <div className="text-3xl leading-none">⚠</div>
          <div className="flex-1 min-w-0">
            <div className="font-bold uppercase tracking-wider text-sm">
              {complianceCount} compliance breach{complianceCount === 1 ? '' : 'es'} open · review now
            </div>
            <div className="text-xs text-red-100 mt-0.5">
              Unresolved breaches accumulate audit risk. The compliance scaffold below shows each one + the action needed.
              CMS / TPMO / ASOP findings are flagged with their citation so you can defend the file.
            </div>
          </div>
          <a
            href="#compliance-scaffold"
            className="bg-white text-red-700 hover:bg-red-50 font-bold text-sm px-4 py-2 rounded shadow shrink-0"
          >
            Review now →
          </a>
        </div>
      )}

      {/* Hero — ENROLL identity + AEP countdown + compliance health. */}
      <div className={`bg-gradient-to-br ${phaseMeta.tone} text-white rounded-xl px-5 py-5`}>
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <div className="text-[10px] uppercase tracking-[0.3em] font-bold text-sky-200/80 flex items-center gap-2">
              <ShieldCheck size={11}/> ENROLL — Medicare / Health Enrollment Agent
            </div>
            <div className="text-lg font-bold mt-1">Compliance scaffolded. AEP de-chaosed.</div>
            <div className="text-xs text-sky-200/80 mt-0.5">{aep?.label || 'AEP window status unavailable'}</div>
          </div>
          <div className="text-right text-sky-100/70 text-xs">
            Snapshot {fmtAgo(data.generated_at)}
          </div>
        </div>

        {/* 4 KPI chips */}
        <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-2">
          <KpiChip
            label="Enrollment ready"
            value={summary.enrollment_ready || 0}
            tone="emerald"
          />
          <KpiChip
            label="AEP re-shop"
            value={summary.reshop_aep_window || 0}
            tone="amber"
            sub={summary.reshop_critical > 0 ? `${summary.reshop_critical} critical` : ''}
          />
          <KpiChip
            label="SEP windows open"
            value={summary.sep_open_count || 0}
            tone="sky"
            sub={summary.sep_closing_soon > 0 ? `${summary.sep_closing_soon} close in ≤7d` : ''}
          />
          <KpiChip
            label="Compliance breaches"
            value={complianceCount}
            tone={complianceCount > 0 ? 'rose' : 'slate'}
            sub={complianceCount === 0 ? 'Clean' : 'Review now'}
          />
        </div>
      </div>

      {/* Compliance health bar — surfaced as its own card because it's
          the differentiator vs every Medicare CRM on the market. */}
      <div id="compliance-scaffold" className={`bg-white border rounded-xl px-5 py-4 scroll-mt-4 ${complianceCount > 0 ? 'border-rose-300 ring-1 ring-rose-100' : 'border-slate-200'}`}>
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div>
            <h3 className="font-bold text-sm text-slate-900 flex items-center gap-2">
              <FileWarning size={14} className={complianceCount > 0 ? 'text-rose-600' : 'text-emerald-600'}/>
              Compliance scaffold {complianceCount > 0 ? '— BREACHES OPEN' : '— clean'}
            </h3>
            {/* BUG-011 followup — the subtitle used to talk about
                SOA capture rate (30d stat) while the badge showed
                complianceCount (open-breach count). Same card,
                different metrics, reading as a copy/badge mismatch.
                Fixed by making the subtitle SPECIFICALLY about the
                breach count when there are any, and only surfacing
                the capture-rate stat when the book is clean. */}
            <div className="text-xs text-slate-500 mt-0.5">
              {(() => {
                const soa     = Number(summary.soa_captured_30d || 0);
                const touched = Number(summary.beneficiaries_touched_30d || 0);
                // When breaches are open, the badge dominates — the
                // subtitle should describe THOSE, not the 30d capture
                // stat that lives in a different data source.
                if (complianceCount > 0) {
                  return `${complianceCount} open breach${complianceCount === 1 ? '' : 'es'} across your Medicare book — review each in the Escalations panel below.`;
                }
                if (touched === 0 && soa === 0) return 'No beneficiary touches in the last 30 days.';
                if (touched === 0 && soa > 0)   return `${soa} SOA${soa === 1 ? '' : 's'} captured in the last 30d — no matching outreach touches found (counted from different sources; investigate).`;
                if (soa === 0)                   return `${touched} beneficiary touch${touched === 1 ? '' : 'es'} in the last 30d — no SOAs captured yet.`;
                return `SOA captured for ${soa} of ${touched} beneficiaries touched in the last 30d (${fmtPct(summary.soa_capture_rate)} capture rate).`;
              })()}
            </div>
          </div>
          {complianceCount === 0 ? (
            <span className="text-[10px] font-bold uppercase tracking-wider bg-emerald-100 text-emerald-800 border border-emerald-200 rounded-full px-2 py-0.5 inline-flex items-center gap-1">
              <CheckCircle2 size={10}/> All clear
            </span>
          ) : (
            <span className="text-[10px] font-bold uppercase tracking-wider bg-rose-100 text-rose-800 border border-rose-200 rounded-full px-2 py-0.5">
              Review {complianceCount}
            </span>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Left: queues — enrollment-ready + re-shop + SEP. */}
        <div className="lg:col-span-2 space-y-4">
          <QueuePanel
            title="Enrollment-ready"
            icon={Users}
            iconTone="text-emerald-700"
            subtitle="Eligibility screened, SOA on file (or being captured). Pick up the phone, enroll."
            rows={(queues.eligibility_ready || []).map(r => ({
              key: r.lead_key,
              name: r.display_name || r.lead_key,
              phone: r.phone_e164,
              status: r.ai_status,
              touched: r.last_ai_touch_at,
              outcome: r.last_ai_outcome,
            }))}
            empty="No prepped enrollees right now. ENROLL is screening; check back after the next inbound batch."
          />
          <QueuePanel
            title="AEP re-shop"
            icon={Calendar}
            iconTone="text-amber-700"
            subtitle="Plan changing in a meaningful way (premium, formulary, network). Annual review call wins these back."
            rows={(queues.reshop || []).map(r => ({
              key: r.policy_id || r.client_id,
              name: r.client_name,
              detail: `${r.carrier || ''} · ${r.plan_name || ''}`.replace(/^ · /, '') || r.plan_type,
              urgencyLabel: r.urgency,
              touched: null,
              daysUntil: r.days_until_expiration,
            }))}
            empty="No plans flagged for re-shop right now."
          />
          <QueuePanel
            title="SEP windows open"
            icon={Calendar}
            iconTone="text-sky-700"
            subtitle="Birthday rule, LIS change, move, lost coverage — limited window to enroll."
            rows={(queues.sep_open || []).map(r => ({
              key: r.id,
              name: r.client_name,
              detail: `${r.sep_kind} · ends ${r.ends_on || '—'}`,
              urgencyLabel: r.days_remaining != null && r.days_remaining <= 7 ? 'closing' : null,
              daysUntil: r.days_remaining,
            }))}
            empty="No SEP windows open right now."
          />
        </div>

        {/* Right: escalations. */}
        <div className="space-y-4">
          <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
            <div className="px-4 py-3 border-b border-slate-100 flex items-center justify-between gap-2">
              <h3 className="font-bold text-sm text-slate-900 flex items-center gap-2"><AlertCircle size={14} className="text-amber-700"/> Escalations</h3>
              <span className={`text-[10px] font-bold uppercase tracking-wider rounded-full px-1.5 py-0.5 border ${escalations.length > 0 ? 'bg-amber-50 text-amber-800 border-amber-200' : 'bg-slate-100 text-slate-600 border-slate-200'}`}>
                {escalations.length}
              </span>
            </div>
            {escalations.length === 0 ? (
              <div className="px-4 py-5 text-center text-xs text-slate-500">
                Nothing escalated. ENROLL is handling routine work.
              </div>
            ) : (
              <div className="divide-y divide-slate-100 max-h-[420px] overflow-y-auto">
                {escalations.map(e => (
                  <EscalationRow
                    key={e.id}
                    escalation={e}
                    onResolve={(status, outcome) => resolveEscalation(e, status, outcome)}
                  />
                ))}
              </div>
            )}
          </div>

          <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
            <div className="px-4 py-3 border-b border-slate-100">
              <h3 className="font-bold text-sm text-slate-900">Workspace shortcuts</h3>
            </div>
            <div className="divide-y divide-slate-100">
              <ShortcutLink onClick={() => onGotoTab('health_clients')} label="Health clients (full list)"/>
              <ShortcutLink onClick={() => onGotoTab('health_enrollment')} label="Enrollment pipeline (T65 / SEP)"/>
              <ShortcutLink onClick={() => onGotoTab('soa')} label="SOA tracker (compliance records)"/>
              <ShortcutLink onClick={() => onGotoTab('health_inbox')} label="Health inbox (inbound)"/>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function KpiChip({ label, value, tone = 'slate', sub }) {
  const tones = {
    emerald: 'bg-emerald-500/20 text-emerald-100 border-emerald-400/30',
    amber:   'bg-amber-500/20   text-amber-100   border-amber-400/30',
    sky:     'bg-sky-500/20     text-sky-100     border-sky-400/30',
    rose:    'bg-rose-500/30    text-rose-100    border-rose-400/40',
    slate:   'bg-white/10       text-white       border-white/20',
  };
  return (
    <div className={`rounded-lg px-3 py-2 border ${tones[tone] || tones.slate}`}>
      <div className="text-2xl font-black tabular-nums">{value}</div>
      <div className="text-[10px] uppercase tracking-wider font-bold opacity-80">{label}</div>
      {sub && <div className="text-[10px] opacity-70 mt-0.5">{sub}</div>}
    </div>
  );
}

function QueuePanel({ title, icon: Icon, iconTone, subtitle, rows, empty }) {
  return (
    <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
      <div className="px-5 py-3 border-b border-slate-100">
        <div className="flex items-center justify-between gap-2">
          <h3 className="font-bold text-sm text-slate-900 flex items-center gap-2"><Icon size={14} className={iconTone}/> {title}</h3>
          <span className="text-[10px] font-bold uppercase tracking-wider bg-slate-100 text-slate-600 border border-slate-200 rounded-full px-1.5 py-0.5">{rows.length}</span>
        </div>
        <div className="text-xs text-slate-500 mt-0.5">{subtitle}</div>
      </div>
      {rows.length === 0 ? (
        <div className="px-5 py-6 text-center text-xs text-slate-500">{empty}</div>
      ) : (
        <div className="divide-y divide-slate-100 max-h-[320px] overflow-y-auto">
          {rows.slice(0, 25).map(r => (
            <div key={r.key} className="px-5 py-2.5 flex items-center gap-3">
              <div className="flex-1 min-w-0">
                <div className="text-sm font-semibold text-slate-900 truncate">{r.name || 'Unknown'}</div>
                {r.detail && <div className="text-[11px] text-slate-500 truncate">{r.detail}</div>}
                {r.outcome && <div className="text-[11px] text-slate-500 truncate">Last: {r.outcome}</div>}
              </div>
              {r.daysUntil != null && (
                <div className={`text-[11px] font-semibold tabular-nums ${r.daysUntil <= 7 ? 'text-rose-700' : 'text-slate-600'}`}>
                  {r.daysUntil}d
                </div>
              )}
              {r.urgencyLabel && (
                <span className={`text-[10px] font-bold uppercase tracking-wider rounded-full px-1.5 py-0.5 border ${
                  r.urgencyLabel === 'closing' || r.urgencyLabel === 'critical'
                    ? 'bg-rose-50 text-rose-800 border-rose-200'
                    : r.urgencyLabel === 'aep_window'
                      ? 'bg-amber-50 text-amber-800 border-amber-200'
                      : 'bg-slate-100 text-slate-700 border-slate-200'
                }`}>
                  {r.urgencyLabel === 'aep_window' ? 'AEP' : r.urgencyLabel}
                </span>
              )}
              {r.touched && <span className="text-[10px] text-slate-400 hidden md:inline">{fmtAgo(r.touched)}</span>}
            </div>
          ))}
          {rows.length > 25 && (
            <div className="px-5 py-2 text-xs text-slate-500 text-center">…and {rows.length - 25} more</div>
          )}
        </div>
      )}
    </div>
  );
}

function EscalationRow({ escalation, onResolve }) {
  const u = URGENCY_META[escalation.urgency] || URGENCY_META.normal;
  return (
    <div className="px-4 py-3">
      <div className="flex items-start gap-2 flex-wrap">
        <span className={`text-[10px] font-bold uppercase tracking-wider rounded-full px-1.5 py-0.5 border shrink-0 ${u.bg} ${u.border} ${u.text}`}>
          {u.label}
        </span>
        <div className="text-sm font-semibold text-slate-900 truncate flex-1">
          {escalation.policy_ref || escalation.lead_id || 'Beneficiary'}
        </div>
      </div>
      <div className="text-xs text-slate-600 mt-1.5">{escalation.summary}</div>
      {escalation.suggested_action && (
        <div className="text-[11px] text-slate-500 mt-1 italic">→ {escalation.suggested_action}</div>
      )}
      <div className="flex items-center gap-2 mt-2">
        <button
          onClick={() => onResolve('resolved', 'agent_handled')}
          className="text-xs font-semibold inline-flex items-center gap-1 px-2 py-1 rounded bg-emerald-50 text-emerald-800 border border-emerald-200 hover:bg-emerald-100"
        >
          <CheckCircle2 size={11}/> Handled
        </button>
        <button
          onClick={() => onResolve('dismissed', 'not_actionable')}
          className="text-xs font-semibold inline-flex items-center gap-1 px-2 py-1 rounded bg-slate-50 text-slate-700 border border-slate-200 hover:bg-slate-100"
        >
          Dismiss
        </button>
        <span className="text-[10px] text-slate-400 ml-auto">{fmtAgo(escalation.created_at)}</span>
      </div>
    </div>
  );
}

function ShortcutLink({ onClick, label }) {
  return (
    <button
      onClick={onClick}
      className="w-full px-4 py-2.5 flex items-center justify-between gap-2 text-left hover:bg-slate-50 transition-colors"
    >
      <span className="text-sm font-semibold text-slate-700">{label}</span>
      <ChevronRight size={14} className="text-slate-400"/>
    </button>
  );
}
